import React, { useState, useEffect } from "react";
import AuthorizationList from "./AuthorizationList";
import CreateAuthorizationForm from "./CreateAuthorizationForm";
import EditAuthorizationForm from "./EditAuthorizationForm";
import { useAuth } from "../../contexts/AuthContext";
import { useAuditContext } from "../../contexts/AuditContext";
import { auditApi } from "../../utils/api";

const AuthorizationPage = () => {
  const [activeTab, setActiveTab] = useState("list");
  const [editingAuth, setEditingAuth] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [hipaaNoticeCollapsed, setHipaaNoticeCollapsed] = useState(
    localStorage.getItem("hipaaNoticeCollapsed") === "true"
  );

  // Get user info from useAuth hook
  const { user } = useAuth();
  const role = user?.role;
  const tenant_id = user?.tenant_id;
  const is_admin = user?.is_admin;
  const { logAuditEvent } = useAuditContext();

  // Log page access for HIPAA compliance
  useEffect(() => {
    const logPageAccess = async () => {
      // Only log if user has access
      if (is_admin || role === "admin") {
        await auditApi.logEntry({
          action: "VIEW_AUTHORIZATION_PAGE",
          entity_type: "AUTHORIZATION",
          entity_id: null,
          purpose_of_use: "OPERATIONS",
          contains_phi: true, // Authorization records contain PHI
        });
      }
    };

    logPageAccess();
  }, [is_admin, role]);

  // Toggle HIPAA notice and save preference
  const toggleHipaaNotice = () => {
    const newState = !hipaaNoticeCollapsed;
    setHipaaNoticeCollapsed(newState);
    localStorage.setItem("hipaaNoticeCollapsed", newState.toString());
  };

  // Ensure only authorized users can access the page
  if (!is_admin && role !== "admin") {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          You do not have permission to access this page.
        </div>
      </div>
    );
  }

  // Handle search functionality
  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  // Handle when authorization is created - redirect to list
  const handleAuthCreated = () => {
    setActiveTab("list");
    // Log successful creation
    auditApi.logEntry({
      action: "CREATE_AUTHORIZATION",
      entity_type: "AUTHORIZATION",
      action_status: "SUCCESS",
      purpose_of_use: "OPERATIONS",
      contains_phi: true,
    });
  };

  // Handle when authorization is updated - redirect to list
  const handleAuthUpdated = () => {
    setEditingAuth(null);
    setActiveTab("list");
    // Log successful update
    auditApi.logEntry({
      action: "UPDATE_AUTHORIZATION",
      entity_type: "AUTHORIZATION",
      entity_id: editingAuth?.authorization_id,
      action_status: "SUCCESS",
      purpose_of_use: "OPERATIONS",
      contains_phi: true,
    });
  };

  // Handle edit button click
  const handleEditAuth = (auth) => {
    setEditingAuth(auth);
    setActiveTab("edit");
  };

  // Handle cancel from edit form
  const handleCancelEdit = () => {
    setEditingAuth(null);
    setActiveTab("list");
  };

  return (
    <div className="container mt-4">
      {/* Collapsible HIPAA Compliance Notice */}
      <div className="alert alert-info mb-3 p-0 overflow-hidden">
        <div
          className="d-flex align-items-center justify-content-between px-3 py-2"
          style={{ cursor: "pointer" }}
          onClick={toggleHipaaNotice}
        >
          <div className="d-flex align-items-center">
            <i className="bi bi-shield-lock me-2"></i>
            <strong>HIPAA Notice</strong>
            {hipaaNoticeCollapsed && (
              <span className="ms-2 text-muted small">
                (Click to expand)
              </span>
            )}
          </div>
          <i
            className={`bi bi-chevron-${hipaaNoticeCollapsed ? "down" : "up"}`}
          ></i>
        </div>
        {!hipaaNoticeCollapsed && (
          <div className="px-3 pb-2 pt-1 border-top">
            This section contains Protected Health Information (PHI). All access
            is logged and monitored for compliance. Only authorized personnel
            should access this information for legitimate business purposes
            related to treatment, payment, or healthcare operations.
          </div>
        )}
      </div>

      <h2 className="mb-4">
        <i className="bi bi-file-earmark-text me-2"></i>
        Authorization Management
      </h2>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "list" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("list");
              setEditingAuth(null);
            }}
          >
            <i className="bi bi-list-ul me-2"></i>
            Authorization List
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "create" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("create");
              setEditingAuth(null);
            }}
          >
            <i className="bi bi-plus-circle me-2"></i>
            Create Authorization
          </button>
        </li>
        {editingAuth && (
          <li className="nav-item">
            <button className="nav-link active">
              <i className="bi bi-pencil me-2"></i>
              Edit Authorization
            </button>
          </li>
        )}
      </ul>

      {/* Tab Content */}
      {activeTab === "list" && (
        <AuthorizationList
          searchConsumer={searchText}
          onEdit={handleEditAuth}
        />
      )}

      {activeTab === "create" && (
        <CreateAuthorizationForm onCreated={handleAuthCreated} />
      )}

      {activeTab === "edit" && editingAuth && (
        <EditAuthorizationForm
          authorization={editingAuth}
          onCancel={handleCancelEdit}
          onUpdated={handleAuthUpdated}
        />
      )}
    </div>
  );
};

export default AuthorizationPage;